import { useAuth } from "@/_core/hooks/useAuth";
import { AutoPlusLogo } from "@/components/brand/autoplus-logo";
import { MobileBackButton } from "@/components/navigation/mobile-back-button";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useSearch } from "wouter";
import {
  Heart,
  User,
  Menu,
  X,
  Search,
  Bell,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { resolveCarImageUrl } from "@/lib/car-image-url";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { formatSiteNumber } from "@shared/currency";
import { cn } from "@/lib/utils";
import { dashboardEntryHref, isAdminUser } from "@/lib/auth-utils";
import { listingsPrefetchInput } from "@/lib/listing-page-snapshot";

type NavItem = {
  href: string;
  label: string;
  active: boolean;
  desktop?: boolean;
};

function navLinkClass(active: boolean) {
  return cn(
    "whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
    active
      ? "bg-[var(--logo-blue-light)] text-[var(--logo-blue)]"
      : "text-black hover:bg-[var(--logo-blue-light)] hover:text-[var(--logo-blue)]",
  );
}

function desktopNavLinkClass(active: boolean) {
  return cn(
    "whitespace-nowrap rounded-full px-3 py-1.5 text-base font-medium transition-colors",
    active
      ? "bg-[var(--logo-blue-light)] text-[var(--logo-blue)]"
      : "text-black hover:bg-[var(--logo-blue-light)] hover:text-[var(--logo-blue)]",
  );
}

function mobileNavLinkClass(active: boolean) {
  return cn(
    navLinkClass(active),
    "flex w-full items-center justify-start rounded-xl px-4 py-3 text-start text-base",
  );
}

export default function Header() {
  const utils = trpc.useUtils();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [pathname, setLocation] = useLocation();
  const search = useSearch();

  const isHomePage = pathname === "/";
  const isAuctionNav =
    search.includes("type=auction") ||
    (pathname.startsWith("/car/") && search.includes("sale=auction"));
  const isFixedNav =
    search.includes("type=fixed") ||
    (pathname.startsWith("/car/") && search.includes("sale=fixed"));
  const isAllCarsNav = pathname === "/listings" && !isAuctionNav && !isFixedNav;

  const isAdmin = Boolean(user && isAdminUser(user));
  const profileHref = isAdmin ? "/dashboard/admin/profile" : "/dashboard/bidder/profile";
  const dashboardHref = dashboardEntryHref(user);
  const accountHref = isAdmin ? dashboardHref : profileHref;
  const notificationsHref = "/dashboard/bidder/notifications";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  const { data: searchResults, isLoading: isSearching } = trpc.cars.list.useQuery(
    { search: debouncedSearch || undefined, limit: 10, offset: 0 },
    { enabled: debouncedSearch.length > 0 && isSearchOpen },
  );

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setLocation(`/listings?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const handleResultClick = (carId: number) => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setLocation(`/car/${carId}`);
  };

  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setLocation(`/listings?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const closeMobileMenu = () => setIsMenuOpen(false);

  const desktopNavItems: NavItem[] = [
    ...(!isHomePage
      ? [{ href: "/", label: "الرئيسية", active: false, desktop: true }]
      : []),
    { href: "/listings?type=fixed", label: "سعر ثابت", active: isFixedNav, desktop: true },
    { href: "/listings?type=auction", label: "المزادات", active: isAuctionNav, desktop: true },
    { href: "/listings", label: "جميع المركبات", active: isAllCarsNav, desktop: true },
  ];

  const mobileNavItems: NavItem[] = [
    ...(!isHomePage ? [{ href: "/", label: "الرئيسية", active: false }] : []),
    { href: "/listings?type=fixed", label: "سعر ثابت", active: isFixedNav },
    { href: "/listings?type=auction", label: "المزادات", active: isAuctionNav },
    { href: "/listings", label: "جميع المركبات", active: isAllCarsNav },
    { href: "/faqs", label: "الأسئلة الشائعة", active: pathname === "/faqs" },
    { href: "/support", label: "الدعم", active: pathname === "/support" },
  ];

  const iconButtonClass =
    "flex size-9 items-center justify-center rounded-full bg-muted/50 text-foreground transition-colors hover:bg-muted sm:size-8";

  return (
    <header className="sticky top-3 z-50 px-3 sm:top-4 sm:px-4">
      <motion.div
        className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-2 rounded-full border border-border/50 bg-white/95 px-3 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.1)] backdrop-blur-md sm:px-4 md:grid md:grid-cols-[auto_1fr_auto] md:gap-3 md:px-5"
        dir="ltr"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="relative z-10 flex shrink-0 items-center gap-1.5 md:justify-self-start">
          {!isHomePage ? <MobileBackButton fallbackHref="/" arrow="ltr" /> : null}
          <Link
            href="/"
            className="flex items-center transition-opacity hover:opacity-80"
          >
            <AutoPlusLogo size="header" className="max-h-9 sm:max-h-10" />
          </Link>
        </div>

        <nav
          className="hidden items-center justify-center gap-0.5 md:flex lg:gap-1"
          aria-label="التنقل الرئيسي"
          dir="rtl"
        >
          {desktopNavItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={desktopNavLinkClass(item.active)}
              onMouseEnter={() => {
                if (item.href.startsWith("/listings")) {
                  void utils.cars.listWithTotal.prefetch(listingsPrefetchInput(item.href));
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <motion.div
          className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2 md:justify-self-end"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className={cn(iconButtonClass, "hidden sm:flex")}
            aria-label="بحث"
          >
            <Search className="size-4" />
          </button>

          {isAuthenticated ? (
            <>
              <Link
                href="/dashboard/bidder/favorites"
                className={cn(iconButtonClass, "hidden sm:flex")}
                aria-label="المفضلة"
              >
                <Heart className="size-4" />
              </Link>
              <Link
                href={notificationsHref}
                className={cn(iconButtonClass, "hidden sm:flex")}
                aria-label="الإشعارات"
              >
                <Bell className="size-4" />
              </Link>
            </>
          ) : null}

          {isAuthenticated ? (
            <Link
              href={accountHref}
              className="flex size-9 items-center justify-center overflow-hidden rounded-full bg-[#0071BC] ring-2 ring-white transition-opacity hover:opacity-90 sm:size-8"
              aria-label={isAdmin ? "نظرة عامة" : "الملف الشخصي"}
            >
              <User className="size-4 text-white" />
            </Link>
          ) : loading ? null : (
            <div className="hidden items-center gap-1.5 sm:flex">
              <Button
                asChild
                size="sm"
                variant="outline"
                className="h-8 rounded-full border-border px-3 text-xs font-medium"
              >
                <Link href="/login">تسجيل الدخول</Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="h-8 rounded-full bg-[#0071BC] px-3 text-xs font-medium text-white hover:bg-[#0071BC]/90"
              >
                <Link href="/register">إنشاء حساب</Link>
              </Button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={cn(iconButtonClass, "md:hidden")}
            aria-label={isMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
          >
            {isMenuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </motion.div>
      </motion.div>

      {isMenuOpen ? (
        <div className="mx-auto mt-2 max-w-5xl overflow-hidden rounded-2xl border border-border/50 bg-white shadow-lg md:hidden">
          <nav className="flex flex-col gap-1 p-3" dir="rtl" aria-label="التنقل الرئيسي">
            {mobileNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className={mobileNavLinkClass(item.active)}
                onMouseEnter={() => {
                  if (item.href.startsWith("/listings")) {
                    void utils.cars.listWithTotal.prefetch(listingsPrefetchInput(item.href));
                  }
                }}
              >
                {item.label}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                setIsSearchOpen(true);
              }}
              className={mobileNavLinkClass(false)}
            >
              بحث عن سيارة
            </button>

            {!isAuthenticated ? (
              <div className="mt-2 flex flex-col gap-2 border-t border-border/60 pt-3">
                <Button asChild variant="outline" className="h-11 w-full rounded-full">
                  <Link href="/login" onClick={closeMobileMenu}>
                    تسجيل الدخول
                  </Link>
                </Button>
                <Button
                  asChild
                  className="h-11 w-full rounded-full bg-[#0071BC] text-white hover:bg-[#0071BC]/90"
                >
                  <Link href="/register" onClick={closeMobileMenu}>
                    إنشاء حساب
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex flex-col gap-2 border-t border-border/60 pt-3">
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link href="/dashboard/bidder/favorites" onClick={closeMobileMenu}>
                    المفضلة
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link href={dashboardHref} onClick={closeMobileMenu}>
                    {isAdmin ? "نظرة عامة" : "لوحة المزايد"}
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full rounded-full"
                  onClick={() => {
                    closeMobileMenu();
                    logout();
                  }}
                >
                  تسجيل الخروج
                </Button>
              </div>
            )}
          </nav>
        </div>
      ) : null}

      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="p-0 sm:max-w-[600px]">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>البحث عن السيارات</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSearchSubmit} className="px-6 pb-6">
            <div className="relative">
              <Search className="absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="ابحث عن سيارة..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl pr-10"
              />
            </div>

            {debouncedSearch.length > 0 ? (
              <div className="mt-4 max-h-[400px] overflow-y-auto">
                {isSearching ? (
                  <p className="py-8 text-center text-muted-foreground">جاري البحث...</p>
                ) : searchResults && searchResults.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {searchResults.map(
                        (car: {
                          id: number;
                          titleAr: string;
                          year: number;
                          mileage: number;
                          fuelType: string;
                          saleType: string;
                          mainImage?: string;
                        }) => (
                          <button
                            key={car.id}
                            type="button"
                            onClick={() => handleResultClick(car.id)}
                            className="w-full rounded-2xl border border-border p-4 text-right transition-colors hover:bg-muted"
                          >
                            <div className="flex items-center gap-4">
                              <div className="min-w-0 flex-1">
                                <h3 className="truncate font-semibold">{car.titleAr}</h3>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {car.year} · {formatSiteNumber(car.mileage)} كم · {car.fuelType}
                                </p>
                              </div>
                              {car.mainImage ? (
                                <img
                                  src={resolveCarImageUrl(car.mainImage)}
                                  alt={car.titleAr}
                                  loading="lazy"
                                  decoding="async"
                                  className="size-16 shrink-0 rounded-xl object-cover"
                                />
                              ) : null}
                            </div>
                          </button>
                        ),
                      )}
                    </div>
                    {searchResults.length >= 10 ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-4 w-full rounded-full"
                        onClick={handleViewAllResults}
                      >
                        عرض جميع النتائج
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <p className="py-8 text-center text-muted-foreground">لا توجد نتائج</p>
                )}
              </div>
            ) : null}
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
}
